import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  format,
  addDays,
  differenceInDays,
  startOfDay,
  startOfMonth,
  endOfMonth,
  subMonths,
  parseISO,
  getWeek,
  isWeekend as isDateWeekend,
  isSameDay,
  isBefore,
  isAfter,
} from 'date-fns';
import { es, enUS } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Skeleton } from '@/components/ui/skeleton';
import {
  ChevronLeft,
  ChevronRight,
  CalendarRange,
  CalendarDays,
  CalendarClock,
  ClipboardList,
  MapPin,
} from 'lucide-react';
import { useInventoryProcesses, type InventoryProcess } from '@/api/hooks/useInventory';

// ============================================================================
// Types
// ============================================================================

interface InventoryBar {
  id: number;
  name: string;
  locationId: number | null;
  locationName: string;
  startDate: Date;
  endDate: Date;
  totalAssets: number;
  scannedAssets: number;
  missingAssets: number;
  progress: number;
  status: InventoryProcess['status'];
  createdBy: string;
}

interface LocationRow {
  locationId: number;
  locationName: string;
  bars: InventoryBar[];
  totalProcesses: number;
  totalAssets: number;
}

interface MonthSpan {
  label: string;
  span: number;
}

// ============================================================================
// Constants
// ============================================================================

const DAY_WIDTH_WEEK = 40;
const DAY_WIDTH_MONTH = 14;
const ROW_HEIGHT = 52;
const HEADER_HEIGHT = 52;
const LOCATION_PANEL_WIDTH = 240;

type ViewMode = 'week' | 'month';

// ============================================================================
// Status color configuration
// ============================================================================

const STATUS_BAR_STYLES: Record<
  InventoryProcess['status'],
  { bg: string; border: string; text: string }
> = {
  Open: {
    bg: 'bg-blue-500',
    border: 'border-blue-600',
    text: 'text-white',
  },
  InProgress: {
    bg: 'bg-amber-400',
    border: 'border-amber-600',
    text: 'text-amber-950',
  },
  Closed: {
    bg: 'bg-emerald-500',
    border: 'border-emerald-600',
    text: 'text-white',
  },
};

const STATUS_LEGEND_COLORS: Record<InventoryProcess['status'], string> = {
  Open: 'bg-blue-500',
  InProgress: 'bg-amber-400',
  Closed: 'bg-emerald-500',
};

// ============================================================================
// Helpers
// ============================================================================

function generateDateRange(start: Date, end: Date): Date[] {
  const dates: Date[] = [];
  let current = startOfDay(start);
  const endDay = startOfDay(end);
  while (!isAfter(current, endDay)) {
    dates.push(current);
    current = addDays(current, 1);
  }
  return dates;
}

function rangesOverlap(
  aStart: Date,
  aEnd: Date,
  bStart: Date,
  bEnd: Date
): boolean {
  return !isAfter(aStart, bEnd) && !isBefore(aEnd, bStart);
}

// ============================================================================
// Main Component
// ============================================================================

export function InventoryScheduler() {
  const { t, i18n } = useTranslation('dashboard');
  const navigate = useNavigate();
  const locale = i18n.language === 'es' ? es : enUS;

  // ---------------------------------------------------------------------------
  // Data fetching
  // ---------------------------------------------------------------------------

  const { data: processData, isLoading } = useInventoryProcesses({ pageSize: 500 });

  // ---------------------------------------------------------------------------
  // State
  // ---------------------------------------------------------------------------

  const [viewMode, setViewMode] = useState<ViewMode>('month');
  const [dateRange, setDateRange] = useState<{ start: Date; end: Date }>(
    () => {
      const now = new Date();
      return {
        start: startOfMonth(subMonths(now, 3)),
        end: endOfMonth(now),
      };
    }
  );

  // ---------------------------------------------------------------------------
  // Derived values
  // ---------------------------------------------------------------------------

  const dayWidth = viewMode === 'week' ? DAY_WIDTH_WEEK : DAY_WIDTH_MONTH;
  const allDays = useMemo(
    () => generateDateRange(dateRange.start, dateRange.end),
    [dateRange]
  );
  const totalTimelineWidth = allDays.length * dayWidth;
  const today = useMemo(() => startOfDay(new Date()), []);

  // ---------------------------------------------------------------------------
  // Data transformation
  // ---------------------------------------------------------------------------

  const locationRows = useMemo<LocationRow[]>(() => {
    if (!processData?.items) return [];

    const grouped = new Map<number, { name: string; bars: InventoryBar[] }>();

    for (const proc of processData.items) {
      const startDate = parseISO(proc.startDate);
      let endDate: Date;
      if (proc.endDate) {
        endDate = parseISO(proc.endDate);
      } else if (proc.status === 'InProgress') {
        endDate = new Date();
      } else {
        endDate = addDays(startDate, 1);
      }

      const progress =
        proc.totalAssets > 0
          ? Math.round((proc.scannedAssets / proc.totalAssets) * 100)
          : 0;

      const locationId = proc.locationId ?? -1;
      const locationName = proc.locationName || t('inventoryScheduler.unassigned');

      const bar: InventoryBar = {
        id: proc.id,
        name: proc.name,
        locationId: proc.locationId,
        locationName,
        startDate: startOfDay(startDate),
        endDate: startOfDay(endDate),
        totalAssets: proc.totalAssets,
        scannedAssets: proc.scannedAssets,
        missingAssets: proc.missingAssets,
        progress,
        status: proc.status,
        createdBy: proc.createdBy,
      };

      if (
        rangesOverlap(bar.startDate, bar.endDate, dateRange.start, dateRange.end)
      ) {
        const existing = grouped.get(locationId);
        if (existing) {
          existing.bars.push(bar);
        } else {
          grouped.set(locationId, { name: locationName, bars: [bar] });
        }
      }
    }

    const rows: LocationRow[] = [];
    for (const [locationId, { name, bars }] of grouped) {
      const totalAssets = bars.reduce((sum, b) => sum + b.totalAssets, 0);
      rows.push({
        locationId,
        locationName: name,
        bars,
        totalProcesses: bars.length,
        totalAssets,
      });
    }

    rows.sort((a, b) => a.locationName.localeCompare(b.locationName));

    return rows;
  }, [processData, dateRange, t]);

  const totalProcesses = useMemo(
    () => locationRows.reduce((sum, row) => sum + row.bars.length, 0),
    [locationRows]
  );

  // ---------------------------------------------------------------------------
  // Month spans for header
  // ---------------------------------------------------------------------------

  const monthSpans = useMemo<MonthSpan[]>(() => {
    const spans: MonthSpan[] = [];
    let currentLabel = '';
    for (const day of allDays) {
      const label = format(day, 'MMMM yyyy', { locale });
      if (label !== currentLabel) {
        spans.push({ label, span: 1 });
        currentLabel = label;
      } else {
        spans[spans.length - 1].span++;
      }
    }
    return spans;
  }, [allDays, locale]);

  // ---------------------------------------------------------------------------
  // Today marker position
  // ---------------------------------------------------------------------------

  const todayOffset = useMemo(() => {
    if (isBefore(today, dateRange.start) || isAfter(today, dateRange.end)) {
      return null;
    }
    const days = differenceInDays(today, startOfDay(dateRange.start));
    return days * dayWidth + dayWidth / 2;
  }, [today, dateRange, dayWidth]);

  // ---------------------------------------------------------------------------
  // Navigation handlers
  // ---------------------------------------------------------------------------

  function handlePrevious() {
    const shift = viewMode === 'week' ? 7 : 30;
    setDateRange((prev) => ({
      start: addDays(prev.start, -shift),
      end: addDays(prev.end, -shift),
    }));
  }

  function handleNext() {
    const shift = viewMode === 'week' ? 7 : 30;
    setDateRange((prev) => ({
      start: addDays(prev.start, shift),
      end: addDays(prev.end, shift),
    }));
  }

  function handleToday() {
    const now = new Date();
    if (viewMode === 'week') {
      const start = addDays(startOfDay(now), -7);
      const end = addDays(startOfDay(now), 14);
      setDateRange({ start, end });
    } else {
      setDateRange({
        start: startOfMonth(subMonths(now, 1)),
        end: endOfMonth(addDays(now, 30)),
      });
    }
  }

  // ---------------------------------------------------------------------------
  // Bar position calculator
  // ---------------------------------------------------------------------------

  function getBarStyle(bar: InventoryBar): React.CSSProperties {
    const rangeStart = startOfDay(dateRange.start);
    const clampedStart = isBefore(bar.startDate, rangeStart)
      ? rangeStart
      : bar.startDate;
    const clampedEnd = isAfter(bar.endDate, dateRange.end)
      ? dateRange.end
      : bar.endDate;

    const leftDays = differenceInDays(clampedStart, rangeStart);
    const durationDays = differenceInDays(clampedEnd, clampedStart) + 1;

    return {
      position: 'absolute' as const,
      left: leftDays * dayWidth,
      width: Math.max(durationDays * dayWidth - 2, 24),
      top: 6,
      height: ROW_HEIGHT - 12,
    };
  }

  // ---------------------------------------------------------------------------
  // Loading state
  // ---------------------------------------------------------------------------

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-64 mt-1" />
        </CardHeader>
        <CardContent className="p-0">
          <div className="flex items-center justify-center h-64">
            <div className="flex flex-col items-center gap-2">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
              <p className="text-sm text-muted-foreground">
                {t('common:loading', 'Loading...')}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // ---------------------------------------------------------------------------
  // Empty state
  // ---------------------------------------------------------------------------

  if (locationRows.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t('inventoryScheduler.title')}</CardTitle>
          <CardDescription>{t('inventoryScheduler.description')}</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <div className="flex items-center justify-center h-64">
            <div className="flex flex-col items-center gap-2 text-muted-foreground">
              <CalendarRange className="h-12 w-12" />
              <p>{t('inventoryScheduler.noProcesses')}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  const totalContentHeight = locationRows.length * ROW_HEIGHT;

  return (
    <Card>
      {/* Card Header with title + summary badges */}
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <CardTitle>{t('inventoryScheduler.title')}</CardTitle>
            <CardDescription>{t('inventoryScheduler.description')}</CardDescription>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant="outline" className="gap-1">
              <ClipboardList className="h-3 w-3" />
              {t('inventoryScheduler.totalProcesses')}: {totalProcesses}
            </Badge>
            <Badge variant="secondary" className="gap-1">
              <MapPin className="h-3 w-3" />
              {locationRows.length} {t('inventoryScheduler.totalLocations')}
            </Badge>
          </div>
        </div>
      </CardHeader>

      <CardContent className="p-0">
        {/* Toolbar */}
        <div className="flex items-center justify-between px-4 py-2 border-y bg-muted/30 flex-wrap gap-2">
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" onClick={handlePrevious}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={handleToday}>
              {t('inventoryScheduler.today')}
            </Button>
            <Button variant="outline" size="sm" onClick={handleNext}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>

          <div className="text-sm text-muted-foreground hidden sm:block">
            {format(dateRange.start, 'd MMM yyyy', { locale })} &mdash;{' '}
            {format(dateRange.end, 'd MMM yyyy', { locale })}
          </div>

          <div className="flex items-center gap-1">
            <Button
              variant={viewMode === 'week' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('week')}
              className="gap-1"
            >
              <CalendarDays className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">
                {t('inventoryScheduler.weekView')}
              </span>
            </Button>
            <Button
              variant={viewMode === 'month' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('month')}
              className="gap-1"
            >
              <CalendarClock className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">
                {t('inventoryScheduler.monthView')}
              </span>
            </Button>
          </div>
        </div>

        {/* Main Gantt Area */}
        <div
          className="grid overflow-hidden"
          style={{
            gridTemplateColumns: `${LOCATION_PANEL_WIDTH}px minmax(0, 1fr)`,
          }}
        >
          {/* Column 1: Fixed Location Panel */}
          <div className="border-r bg-background">
            <div
              className="flex items-center px-3 border-b bg-muted/50 font-medium text-xs uppercase tracking-wider text-muted-foreground"
              style={{ height: HEADER_HEIGHT }}
            >
              {t('inventoryScheduler.location')}
            </div>
            {locationRows.map((row) => (
              <div
                key={row.locationId}
                className="flex items-center px-3 border-b hover:bg-muted/30 transition-colors gap-2"
                style={{ height: ROW_HEIGHT }}
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">
                    {row.locationName}
                  </p>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span>
                      {row.totalProcesses} {t('inventoryScheduler.processes')}
                    </span>
                    <span className="text-primary font-medium">
                      {row.totalAssets} {t('inventoryScheduler.assets')}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Column 2: Scrollable Timeline */}
          <div className="overflow-x-auto min-w-0">
            <div
              style={{
                width: totalTimelineWidth,
                minHeight: totalContentHeight + HEADER_HEIGHT,
              }}
            >
              {/* Timeline Header */}
              <div
                className="border-b bg-background"
                style={{ height: HEADER_HEIGHT }}
              >
                {/* Month labels row */}
                <div
                  className="flex border-b"
                  style={{ height: HEADER_HEIGHT / 2 }}
                >
                  {monthSpans.map((month, i) => (
                    <div
                      key={i}
                      className="flex items-center justify-center border-r text-xs font-medium bg-muted/30 truncate px-1"
                      style={{ width: month.span * dayWidth }}
                    >
                      {month.span * dayWidth > 60 ? month.label : ''}
                    </div>
                  ))}
                </div>
                {/* Day/Week numbers row */}
                <div className="flex" style={{ height: HEADER_HEIGHT / 2 }}>
                  {allDays.map((day, i) => {
                    const weekend = isDateWeekend(day);
                    const isToday = isSameDay(day, today);

                    if (viewMode === 'week') {
                      return (
                        <div
                          key={i}
                          className={cn(
                            'flex items-center justify-center border-r text-[10px]',
                            weekend && 'bg-muted/50 text-muted-foreground',
                            isToday && 'bg-primary/10 font-bold text-primary'
                          )}
                          style={{ width: dayWidth }}
                        >
                          {format(day, 'd', { locale })}
                        </div>
                      );
                    }

                    const isMonday = day.getDay() === 1;
                    return (
                      <div
                        key={i}
                        className={cn(
                          'flex items-center justify-center border-r text-[9px]',
                          weekend && 'bg-muted/50',
                          isToday && 'bg-primary/10 font-bold text-primary'
                        )}
                        style={{ width: dayWidth }}
                      >
                        {isMonday ? `W${getWeek(day, { locale })}` : ''}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Grid rows + bars */}
              <div className="relative">
                {/* Background columns (weekend shading) */}
                <div
                  className="absolute inset-0 pointer-events-none"
                  style={{ height: totalContentHeight }}
                >
                  {allDays.map((day, i) => {
                    const weekend = isDateWeekend(day);
                    const isToday = isSameDay(day, today);
                    if (!weekend && !isToday) return null;
                    return (
                      <div
                        key={i}
                        className={cn(
                          'absolute top-0',
                          weekend && 'bg-muted/30',
                          isToday && 'bg-primary/5'
                        )}
                        style={{
                          left: i * dayWidth,
                          width: dayWidth,
                          height: totalContentHeight,
                        }}
                      />
                    );
                  })}
                </div>

                {/* Today marker */}
                {todayOffset !== null && (
                  <div
                    className="absolute z-20 pointer-events-none"
                    style={{
                      left: todayOffset,
                      top: 0,
                      height: totalContentHeight,
                    }}
                  >
                    <div className="absolute -top-1 -translate-x-1/2 w-2.5 h-2.5 rounded-full bg-red-500" />
                    <div
                      className="absolute top-0 -translate-x-px w-0.5 bg-red-500"
                      style={{ height: totalContentHeight }}
                    />
                  </div>
                )}

                {/* Timeline row lanes */}
                {locationRows.map((row) => (
                  <div
                    key={row.locationId}
                    className="relative border-b"
                    style={{ height: ROW_HEIGHT }}
                  >
                    {row.bars.map((bar) => {
                      const style = getBarStyle(bar);
                      const barWidth = style.width as number;
                      const statusStyle = STATUS_BAR_STYLES[bar.status];

                      return (
                        <TooltipProvider key={bar.id} delayDuration={150}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div
                                className={cn(
                                  'absolute cursor-pointer rounded-sm border shadow-sm transition-all hover:shadow-md hover:z-10 hover:brightness-110',
                                  'flex items-center justify-center text-[10px] font-medium truncate',
                                  statusStyle.bg,
                                  statusStyle.border,
                                  statusStyle.text
                                )}
                                style={style}
                                onClick={() => navigate(`/inventory/${bar.id}`)}
                              >
                                {barWidth > 50 && (
                                  <span className="truncate px-1">
                                    {bar.scannedAssets}/{bar.totalAssets}
                                  </span>
                                )}
                                {bar.status === 'InProgress' &&
                                  bar.progress > 0 &&
                                  bar.progress < 100 && (
                                    <div
                                      className="absolute bottom-0 left-0 h-1 bg-white/40 rounded-b-sm"
                                      style={{
                                        width: `${bar.progress}%`,
                                      }}
                                    />
                                  )}
                              </div>
                            </TooltipTrigger>
                            <TooltipContent
                              side="top"
                              className="max-w-xs bg-popover text-popover-foreground border shadow-md p-3"
                            >
                              <div className="space-y-1.5">
                                <p className="font-semibold text-sm">
                                  {bar.name}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {bar.locationName}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {format(bar.startDate, 'd MMM yyyy', {
                                    locale,
                                  })}{' '}
                                  &mdash;{' '}
                                  {format(bar.endDate, 'd MMM yyyy', {
                                    locale,
                                  })}
                                </p>
                                <div className="flex items-center gap-2 text-xs">
                                  <span>
                                    {t('inventoryScheduler.scanned')}:{' '}
                                    <strong>{bar.scannedAssets}</strong>
                                  </span>
                                  <span>
                                    {t('inventoryScheduler.total')}:{' '}
                                    <strong>{bar.totalAssets}</strong>
                                  </span>
                                  {bar.missingAssets > 0 && (
                                    <span className="text-red-600">
                                      {t('inventoryScheduler.missing')}:{' '}
                                      <strong>{bar.missingAssets}</strong>
                                    </span>
                                  )}
                                </div>
                                {bar.progress > 0 && (
                                  <p className="text-xs text-muted-foreground">
                                    {t('inventoryScheduler.progress')}: {bar.progress}%
                                  </p>
                                )}
                                {bar.createdBy && (
                                  <p className="text-xs text-muted-foreground">
                                    {t('inventoryScheduler.createdBy')}: {bar.createdBy}
                                  </p>
                                )}
                                <Badge
                                  variant="outline"
                                  className={cn(
                                    'text-[10px]',
                                    statusStyle.bg,
                                    statusStyle.text,
                                    'border-transparent'
                                  )}
                                >
                                  {t(
                                    `inventoryScheduler.status.${bar.status}`,
                                    bar.status
                                  )}
                                </Badge>
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Footer Legend */}
        <div className="flex items-center gap-4 px-4 py-2.5 border-t bg-muted/30 text-xs flex-wrap">
          {(
            Object.entries(STATUS_LEGEND_COLORS) as [
              InventoryProcess['status'],
              string,
            ][]
          ).map(([status, colorClass]) => (
            <div key={status} className="flex items-center gap-1.5">
              <div className={cn('w-3 h-3 rounded-sm', colorClass)} />
              <span className="text-muted-foreground">
                {t(`inventoryScheduler.status.${status}`, status)}
              </span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
